import React, { SyntheticEvent, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { withTheme } from 'styled-components'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { darken } from 'polished'

import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Grid,
  Hidden,
  IconButton as MuiIconButton,
  InputBase,
  MenuList,
  MenuItem,
  AppBar as MuiAppBar,
  Paper,
  Popper,
  Toolbar,
} from '@material-ui/core'

import {
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
} from '@material-ui/icons'

import { Search as SearchIcon, Power } from 'react-feather'
import { AccessRequired } from 'src/components/AccessRequired'
import { StagingBadge } from 'src/components/StagingBadge'
import useProfileContext from 'src/context/ProfileContext'

const AppBar = styled(MuiAppBar)`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};
  box-shadow: ${(props) => props.theme.shadows[1]};
`

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up('xs')} {
    display: block;
  }
`

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)}px;
    padding-right: ${(props) => props.theme.spacing(2.5)}px;
    padding-bottom: ${(props) => props.theme.spacing(2.5)}px;
    padding-left: ${(props) => props.theme.spacing(12)}px;
    width: 100%;
  }
`

const MenuPaper = styled(Paper)`
  border: 1px;
`

const UserMenu = () => {
  const [anchorMenu, setAnchorMenu] = useState<(EventTarget & Element) | null>(null)

  const history = useHistory()

  const toggleMenu = (event: SyntheticEvent) => {
    if (anchorMenu) {
      setAnchorMenu(null)
    } else {
      setAnchorMenu(event.currentTarget)
    }
  }

  const closeMenu = () => {
    setAnchorMenu(null)
  }

  const navigate = (path: string) => () => history.push(path)
  const v2 = (path: string) => () =>
    window.location.assign(`/v2${path}`)
  const newTab = (path: string) => () => window.open(path, '_newtab')
  const profileContext = useProfileContext()
  useEffect(() => {
    profileContext.updateProfile()
  }, [profileContext])

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <Box>
        <IconButton
          aria-owns={anchorMenu ? 'menu-appbar' : undefined}
          aria-haspopup="true"
          onClick={toggleMenu}
          color="inherit"
        >
          {profileContext.profile ? (
            <Avatar
              alt={profileContext.profile?.fullName}
              src={profileContext.profile?.profileImage}
            />
          ) : (
            <Power />
          )}
        </IconButton>
        <Popper open={Boolean(anchorMenu)} anchorEl={anchorMenu} placement="bottom">
          <MenuPaper>
            <MenuList id="menu-appbar">
              <MenuItem onClick={newTab('/client-api/docs')}>
                Client API Docs <ExitToAppIcon />
              </MenuItem>
              <MenuItem onClick={newTab('/internal/client-api/docs')}>
                Legacy API Docs <ExitToAppIcon />
              </MenuItem>
              <MenuItem onClick={newTab('/internal/client-api/docs')}>
                Internal Client API Docs <ExitToAppIcon />
              </MenuItem>
              <MenuItem onClick={newTab('/internal/supply-chain-api/docs')}>
                Internal Supply Chain API Docs <ExitToAppIcon />
              </MenuItem>
              <Divider />
              <MenuItem onClick={navigate('/settings')}>My Profile</MenuItem>
              <AccessRequired staff logout={false}>
                <MenuItem onClick={navigate('/admin/mask')}>Mask</MenuItem>
              </AccessRequired>
              <AccessRequired masked logout={false}>
                <MenuItem onClick={navigate('/admin/unmask')}>Unmask</MenuItem>
              </AccessRequired>
              <AccessRequired staff logout={false}>
                <MenuItem onClick={v2('/admin/supply-chain')}>Supply Chain Settings</MenuItem>
              </AccessRequired>
              <AccessRequired staff logout={false}>
                <MenuItem onClick={v2('/admin/devices')}>Manage Devices</MenuItem>
              </AccessRequired>
              <Divider />
              <MenuItem onClick={navigate('/auth/logout')}>Sign out</MenuItem>
            </MenuList>
          </MenuPaper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

interface Props {
  onDrawerToggle: () => void
  theme: Theme
}

const Header = (props: Props) => {
  const { onDrawerToggle } = props
  const profileContext = useProfileContext()
  useEffect(() => {
    profileContext.updateProfile()
  }, [profileContext])

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Grid container alignItems="center">
            <Hidden mdUp>
              <Grid item>
                <IconButton color="inherit" aria-label="Open drawer" onClick={onDrawerToggle}>
                  <MenuIcon />
                </IconButton>
              </Grid>
            </Hidden>
            <AccessRequired masked logout={false}>
              <Grid item xs>
                <span style={{ color: 'orange' }}>
                  <ReportProblemOutlinedIcon /> Masked as {profileContext.profile?.email}
                </span>
              </Grid>
            </AccessRequired>
            <Grid item xs>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <Input placeholder="Search" />
              </Search>
            </Grid>
            <Grid item>
              <StagingBadge />
            </Grid>
            <Grid item>
              <UserMenu />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </>
  )
}

export default withTheme(Header)
